if (!customElements.get('collection-infinite-scroll')) {
  const PREFETCH_MARGIN = '800px 0px';
  const HEADER_OFFSET = 120;
  const MAX_RETRIES = 3;
  // Divisible by 2, 3 and 4 columns, so a filled row stays filled when the filter panel changes the column count
  const ROW_CELLS = 12;
  const SALE_FIRST_PREFETCH_PAGES = 2;
  const SALE_FIRST_DEFAULT_PAGE_SIZE = 24;
  const SALE_FIRST_MAX_PAGES_PER_REVEAL = 3;
  const SALE_FIRST_RETRY_DELAY_MS = 1000;
  const SALE_FIRST_MAX_RETRY_DELAY_MS = 30000;

  customElements.define('collection-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.lastLoaded = parseInt(this.dataset.currentPage, 10) || 1;
      this.maxEmptyPages = parseInt(this.dataset.maxEmptyPages, 10) || 0;
      this.mergePages = this.hasAttribute('data-merge-pages');
      this.saleFirst = this.hasAttribute('data-sale-first');

      this.spinner = this.querySelector('.collection-infinite__spinner');
      this.sentinel = this.querySelector('.collection-infinite__sentinel');
      this.status = this.querySelector('.collection-infinite__status');
      this.loadMoreButton = this.querySelector('.collection-infinite__load-more');
      this.announcedAny = false;

      this.loading = false;
      this.retries = 0;
      this.emptyPages = 0;
      this.done = !this.sectionId || !this.sentinel || this.lastLoaded >= this.totalPages;
      this.scrollFrame = null;
      this.anchors = null;
      this.mergeTarget = null;

      this.abortController = new AbortController();

      if (this.saleFirst) this.initSaleFirst();

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => this.loadNext(), {
          signal: this.abortController.signal,
        });
      }

      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.syncUrlPage();
        });
      };
      window.addEventListener('scroll', this.onScroll, {
        passive: true,
        signal: this.abortController.signal,
      });

      if (this.done) {
        this.finish();
      } else {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: PREFETCH_MARGIN }
        );
        this.observer.observe(this.sentinel);
      }

      this.syncUrlPage();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
      clearTimeout(this.retryTimer);
      if (this.abortController) this.abortController.abort();
    }

    pageUrl(page) {
      const params = new URLSearchParams(window.location.search);
      params.delete('sections');
      params.set('section_id', this.sectionId);
      params.set('page', String(page));
      return `${window.location.pathname}?${params.toString()}`;
    }

    async loadNext() {
      if (this.saleFirst) return this.revealNextSaleFirst();
      if (this.loading || this.done) return;

      const page = this.lastLoaded + 1;
      if (page > this.totalPages) {
        this.finish();
        return;
      }

      this.setLoading(true);

      try {
        const response = await fetch(this.pageUrl(page), {
          signal: this.abortController.signal,
          priority: 'low',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const grid = template.content.querySelector('.collection__grid-items');

        const parent = this.parentNode;
        if (!parent) {
          this.finish();
          return;
        }

        if (!grid || !grid.querySelector('.grid-item:not(.info-card)')) {
          this.emptyPages += 1;
          this.lastLoaded = page;
          if (this.emptyPages > this.maxEmptyPages || page >= this.totalPages) this.finish();
          return;
        }

        grid.removeAttribute('id');
        grid.classList.remove('loading');

        const target =
          this.mergePages && (this.mergeTarget ||= parent.querySelector('.collection__grid-items'));

        let added;
        if (target) {
          if (grid.firstElementChild) grid.firstElementChild.dataset.page = String(page);
          added = [...grid.children];
          target.append(...added);
        } else {
          grid.dataset.page = String(page);
          added = this.fillLastRow(parent, grid);
          if (grid.children.length) {
            parent.insertBefore(grid, this);
            added.push(grid);
          }
        }

        this.lastLoaded = page;
        this.retries = 0;
        this.emptyPages = 0;
        this.anchors = null;

        this.reInitLazy(added);
        this.syncUrlPage();
        this.announceAdded(added);

        if (page >= this.totalPages) this.finish();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Collection infinite scroll fetch failed:', error);
        this.retries += 1;
        if (this.retries >= MAX_RETRIES) this.finish();
      } finally {
        this.setLoading(false);
        if (!this.done && this.observer) {
          this.observer.unobserve(this.sentinel);
          this.observer.observe(this.sentinel);
        }
      }
    }

    // Hidden products leave a page short of full rows, so the next page's first cards complete them
    fillLastRow(parent, nextGrid) {
      // Info cards sit on a fixed grid-row, so taking cards from above them would leave empty rows
      if (nextGrid.querySelector('.info-card')) return [];

      const grids = parent.querySelectorAll('.collection__grid-items');
      const previous = grids[grids.length - 1];
      if (!previous) return [];

      const style = getComputedStyle(previous);
      const trackWidth = parseFloat(style.gridTemplateColumns);
      if (!trackWidth) return [];
      const gap = parseFloat(style.columnGap) || 0;

      let cells = 0;
      [...previous.children].forEach((child) => {
        const width = child.getBoundingClientRect().width;
        if (width) cells += Math.max(1, Math.round((width + gap) / (trackWidth + gap)));
      });

      const missing = (ROW_CELLS - (cells % ROW_CELLS)) % ROW_CELLS;
      const moved = [...nextGrid.querySelectorAll(':scope > .grid-item')].slice(0, missing);
      previous.append(...moved);
      return moved;
    }

    initSaleFirst() {
      this.pageSize = parseInt(this.dataset.pageSize, 10) || SALE_FIRST_DEFAULT_PAGE_SIZE;
      this.grid = this.parentElement && this.parentElement.querySelector('.collection__grid-items');
      this.saleCards = [];
      this.restCards = [];
      this.pageRequests = new Map();
      this.seenCardIds = new Set();

      const held = this.parentElement && this.parentElement.querySelector('template[data-sale-first-held]');
      if (held) {
        this.restCards.push(...held.content.children);
        held.remove();
      }

      const shownCards = this.grid ? [...this.grid.querySelectorAll(':scope > .grid-item')] : [];
      shownCards.concat(this.restCards).forEach((card) => {
        if (card.id) this.seenCardIds.add(card.id);
      });

      window.addEventListener('online', () => this.retrySaleFirstNow(), {
        signal: this.abortController.signal,
      });
    }

    retrySaleFirstNow() {
      if (!this.retryTimer) return;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      if (!this.done && this.observer) this.observer.observe(this.sentinel);
    }

    appendSaleFirstCards(cards) {
      if (!cards.length) return;
      this.grid.append(...cards);
      this.reInitLazy(cards);
      this.announceAdded(cards);
    }

    fetchSaleFirstPage(page) {
      if (!this.pageRequests.has(page)) {
        const request = fetch(this.pageUrl(page), {
          signal: this.abortController.signal,
          priority: 'low',
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const template = document.createElement('template');
            template.innerHTML = html;
            const grid = template.content.querySelector('.collection__grid-items');
            return grid ? [...grid.querySelectorAll(':scope > .grid-item')] : [];
          });
        request.catch(() => this.pageRequests.delete(page));
        this.pageRequests.set(page, request);
      }
      return this.pageRequests.get(page);
    }

    async sortNextSaleFirstPage() {
      const page = this.lastLoaded + 1;
      const lastPrefetched = Math.min(page + SALE_FIRST_PREFETCH_PAGES, this.totalPages);
      for (let ahead = page + 1; ahead <= lastPrefetched; ahead += 1) this.fetchSaleFirstPage(ahead);

      const cards = await this.fetchSaleFirstPage(page);
      this.pageRequests.delete(page);
      cards.forEach((card) => {
        // A product can shift between pages while they are fetched
        if (card.id) {
          if (this.seenCardIds.has(card.id)) return;
          this.seenCardIds.add(card.id);
        }
        (card.hasAttribute('data-sale-colors') ? this.saleCards : this.restCards).push(card);
      });
      this.lastLoaded = page;
      this.retries = 0;
    }

    // Every Sale card of the collection goes before any other card, so the rest wait until the last page is sorted.
    async revealNextSaleFirst() {
      if (this.loading || this.done) return;
      if (!this.grid) {
        this.finish();
        return;
      }

      this.setLoading(true);
      let retryDelay = 0;

      try {
        let sortedPages = 0;
        while (
          this.lastLoaded < this.totalPages &&
          this.saleCards.length < this.pageSize &&
          sortedPages < SALE_FIRST_MAX_PAGES_PER_REVEAL
        ) {
          await this.sortNextSaleFirstPage();
          sortedPages += 1;
        }

        const batch = this.saleCards.splice(0, this.pageSize);
        if (this.lastLoaded >= this.totalPages && batch.length < this.pageSize) {
          batch.push(...this.restCards.splice(0, this.pageSize - batch.length));
        }
        this.appendSaleFirstCards(batch);

        if (this.lastLoaded >= this.totalPages && !this.saleCards.length && !this.restCards.length) this.finish();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Collection infinite scroll fetch failed:', error);
        // The page is retried until it loads, so no product goes missing or out of order
        this.retries += 1;
        retryDelay = Math.min(SALE_FIRST_RETRY_DELAY_MS * 2 ** (this.retries - 1), SALE_FIRST_MAX_RETRY_DELAY_MS);
      } finally {
        this.setLoading(false);
        if (!this.done && this.observer) {
          this.observer.unobserve(this.sentinel);
          if (retryDelay) {
            this.retryTimer = setTimeout(() => this.retrySaleFirstNow(), retryDelay);
          } else {
            this.observer.observe(this.sentinel);
          }
        }
      }
    }

    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = message;
      this.announcedAny = true;
    }

    announceAdded(added) {
      let count = 0;
      added.forEach((node) => {
        if (node.matches && node.matches('.item-product:not(.info-card)')) count += 1;
        else if (node.querySelectorAll)
          count += node.querySelectorAll('.item-product:not(.info-card)').length;
      });
      if (count) this.announce(`${count} more product${count === 1 ? '' : 's'} loaded.`);
    }

    reInitLazy(added) {
      const $ = window.jQuery;
      if (!$ || !$.fn || !$.fn.Lazy) return;
      const $added = $(added);
      const $lazy = $added.filter('.Lazy').add($added.find('.Lazy'));
      if ($lazy.length) $lazy.Lazy({ effect: 'fadeIn', visibilityOnly: true });
    }

    syncUrlPage() {
      if (this.saleFirst) return;
      const root = this.parentElement;
      if (!root) return;

      let anchors = this.anchors;
      if (!anchors) {
        anchors = root.querySelectorAll(
          '.collection__grid-items[data-page], .collection__grid-items > [data-page]'
        );
        if (!anchors.length) return;
        this.anchors = anchors;
      }

      let page = anchors[0].dataset.page;
      for (let i = anchors.length - 1; i > 0; i -= 1) {
        if (anchors[i].getBoundingClientRect().top <= HEADER_OFFSET) {
          page = anchors[i].dataset.page;
          break;
        }
      }

      const params = new URLSearchParams(window.location.search);
      if ((params.get('page') || '1') === page) return;

      if (page === '1') {
        params.delete('page');
      } else {
        params.set('page', page);
      }

      const search = params.toString();
      const url = `${window.location.pathname}${search ? `?${search}` : ''}`;

      history.replaceState(history.state, '', url);
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      this.spinner?.toggleAttribute('hidden', !isLoading);
      if (!this.done) this.loadMoreButton?.toggleAttribute('disabled', isLoading);
    }

    finish() {
      this.done = true;
      if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
      this.sentinel?.toggleAttribute('hidden', true);
      this.loadMoreButton?.toggleAttribute('hidden', true);
      if (this.announcedAny) this.announce('All products loaded.');
      this.setLoading(false);
    }
  });
}
